import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * This browser on the account's list of devices (the app's Settings → Where you're signed in; firestore.rules
 * `users/{uid}/devices`): one record, by an id this browser keeps, named ("Chrome on Mac") and saying when it was last
 * used. Routr Dispatch gets no pushes, so it holds no address. Deleted from another device, it signs this browser out
 * (session.ts).
 */

const ID_KEY = 'routr-device';
// The account whose list this browser is on, once the server has shown its record: only then can the record going
// mean another device signed it out.
const LISTED_KEY = 'routr-device-listed';

const kept = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      // A private window keeps nothing.
    }
  },
  drop: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // A private window keeps nothing.
    }
  },
};

/** This browser's id, made once. */
export function deviceId(): string {
  const had = kept.get(ID_KEY);
  if (had && /^[A-Za-z0-9-]{8,64}$/.test(had)) return had;
  const made = crypto.randomUUID();
  kept.set(ID_KEY, made);
  return made;
}

/** "Chrome on Mac", "Safari on iPhone", "Edge on Windows": the browser, and what it runs on. */
export function browserName(ua: string): string {
  const browser = /Edg(e|iOS|A)?\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\/|FxiOS\//.test(ua)
        ? 'Firefox'
        : /Chrome\/|CriOS\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'A browser';
  const on = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /CrOS/.test(ua)
          ? 'Chromebook'
          : /Macintosh|Mac OS X/.test(ua)
            ? 'Mac'
            : /Windows/.test(ua)
              ? 'Windows'
              : /Linux/.test(ua)
                ? 'Linux'
                : null;
  return on ? `${browser} on ${on}` : browser;
}

let leaving = false;

/** Signed in: this browser on the account's list, its last use brought up to date; [onRemoved] once another device signs it out. What stops watching. */
export function listDevice(uid: string, onRemoved: () => void): () => void {
  leaving = false;
  const ref = doc(db, 'users', uid, 'devices', deviceId());
  const listed = () => kept.get(LISTED_KEY) === uid;
  void setDoc(
    ref,
    {
      platform: 'web',
      name: browserName(navigator.userAgent).slice(0, 80),
      os: 'Routr Dispatch',
      seenAt: serverTimestamp(),
      // Only while it isn't listed: a record another device deleted can't be made again here (the rules want it).
      ...(listed() ? {} : { signedInAt: serverTimestamp() }),
    },
    { merge: true },
  ).catch(() => {});
  let gone = false;
  return onSnapshot(
    ref,
    { includeMetadataChanges: true },
    (snap) => {
      if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
      if (snap.exists()) {
        if (!listed()) kept.set(LISTED_KEY, uid);
      } else if (listed() && !leaving && !gone) {
        gone = true;
        forgetHere();
        onRemoved();
      }
    },
    () => {},
  );
}

/** What this browser kept of its place on the list, once it's gone. */
function forgetHere() {
  leaving = true;
  kept.drop(LISTED_KEY);
}

/** Signing out here: this browser off the account's list (5 s at most, so signing out never waits on the network). */
export async function unlistDevice(uid: string): Promise<void> {
  forgetHere();
  await Promise.race([deleteDoc(doc(db, 'users', uid, 'devices', deviceId())), new Promise((done) => setTimeout(done, 5000))]).catch(() => {});
}
